import { useSignOut } from '@/features/authentication/services/authMutations';
import { useAuthStore } from '@/features/authentication/stores/authStore';
import { SidebarMenuButton } from '@/shared/components/AppSidebar/SidebarMenuButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { localeLabels, supportedLocales } from '@/shared/config/i18n';
import {
  accountAvatarVariants,
  accountEmailVariants,
  accountFooterVariants,
  accountInitialsVariants,
  accountNameVariants,
  accountTriggerVariants,
} from '@/shared/config/sidebar';
import { setPreferredLocale } from '@/shared/i18n';
import { LOGIN_ROUTE } from '@/shared/lib/authSession';
import { confirm } from '@/shared/lib/confirm';
import type { SupportedLocale } from '@/shared/types/i18n';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Languages, LogOut } from 'lucide-react';

/**
 * Signed-in user trigger + account dropdown. Pinned to the bottom of the
 * desktop sidebar and the mobile full-screen menu (`mt-auto`); mobile uses a
 * larger, page-like profile row.
 */
export function AccountMenu({ mobile = false }: { mobile?: boolean } = {}) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const user = useAuthStore((s) => s.user);

  const dispatch = async () => {
    const confirmed = await confirm({
      title: t`Sign out?`,
      description: t`You'll need to sign in again to access your workspace.`,
      confirmLabel: t`Sign out`,
      variant: 'destructive',
    });
    if (confirmed) {
      signOut.mutate(undefined, {
        onSuccess: () => navigate({ to: LOGIN_ROUTE, replace: true }),
      });
    }
  };

  if (!user) return null;

  // Fall back to the email local-part, then a generic label, so the row always
  // has a name and initials.
  const displayName = user.displayName?.trim() || user.email?.split('@')[0] || t`User`;
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const activeLocale = supportedLocales.includes(i18n.locale as SupportedLocale)
    ? (i18n.locale as SupportedLocale)
    : 'en';

  return (
    <div className={accountFooterVariants({ mobile })}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="default"
            mobile={mobile}
            aria-label={t`Open account menu`}
            className={accountTriggerVariants({ mobile })}
          >
            <div className={accountAvatarVariants({ mobile })}>
              {user.photoURL ? (
                <img src={user.photoURL} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className={accountInitialsVariants({ mobile })}>{initials}</span>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0">
              <span className={accountNameVariants({ mobile })}>{displayName}</span>
              <span className={accountEmailVariants({ mobile })}>{user.email}</span>
            </div>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-52 max-w-80"
          side={mobile ? 'bottom' : 'top'}
          align="start"
          sideOffset={4}
        >
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <Languages className="size-4 shrink-0" />
              <Trans>Language</Trans>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={activeLocale}>
                {supportedLocales.map((locale) => (
                  <DropdownMenuRadioItem
                    key={locale}
                    value={locale}
                    onSelect={() => setPreferredLocale(locale)}
                  >
                    {localeLabels[locale]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            onSelect={() => void dispatch()}
            disabled={signOut.isPending}
            className="gap-2 text-destructive focus:text-destructive [&_svg]:size-4"
          >
            <LogOut />
            <Trans>Sign out</Trans>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
